/**
 * OnboardingWizard.js — first-login profile setup wizard.
 *
 * Triggered by authContext's `isNewUser === true` (set when
 * syncUserToDatabase.php just inserted the row, or when getUserProfile.php
 * reports onboarded_at IS NULL). Mounts inside MapComponent so the map is
 * already visible behind the modal — Skip just dismisses to the map.
 *
 * Steps (all skippable individually; the wizard itself can also be skipped
 * outright via the "Skip for now" footer link):
 *
 *   1. Name / type-of-org / website
 *   2. Bio
 *   3. Profile photo
 *   4. Confirm (recap card)
 *
 * Persistence model:
 *   - State is held locally in `draft`.
 *   - We only PATCH /updateUserProfile.php on Finish (or implicitly on
 *     Skip-from-final-step), not on every Next, so the user can back-out
 *     freely without leaving partial rows behind.
 *   - The PATCH writes onboarded_at as a side effect (server-side), so
 *     reloading the page will not re-show the wizard.
 *   - Skip-without-PATCH still flips local isNewUser to false, but
 *     onboarded_at remains NULL on the server — so the wizard reappears
 *     on the user's next login. That's intentional; it's how we honour the
 *     "skippable but reminding" model from the spec.
 *
 * Photo upload reuses uploadFiles.php with upload_target=profile_photo,
 * which returns the public GCS URL. We persist that URL on users.photo_url
 * via updateUserProfile.php on Finish. The uploadFiles.php branch for
 * profile_photo deliberately does NOT insert into the `files` table.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Modal, Fade } from '@mui/material';
import { useAuth } from '../authContext';
import { useProject } from '../ProjectContext';
import { apiCall, ApiError } from '../api';
import { ORG_TYPE, ORG_TYPE_OPTIONS } from '../utils/constants';
import './OnboardingWizard.css';

const STEPS = ['identity', 'bio', 'photo', 'confirm'];

const STEP_LABELS = {
  identity: 'Tell us about you',
  bio: 'What do you want to share?',
  photo: 'Add a profile photo',
  confirm: 'Looks good?',
};

const STEP_DESCRIPTIONS = {
  identity:
    "Help others know who's adding stories. You can change any of this later from your profile.",
  bio:
    "Share a sentence or two about yourself, or what interests you about history and mapping. Optional, but it helps stories feel personal.",
  photo:
    'Upload a profile photo. Square images look best. You can skip and add one later.',
  confirm:
    "Here's what we'll save to your profile. Finish to save, or step back to make changes.",
};

const PROMPT_PLACEHOLDER =
  'What do you want to share about yourself, or what interests you about history and mapping?';

// Keep client-side limits in lockstep with updateUserProfile.php so we
// never POST a body the server will reject as 400.
const MAX_NAME = 255;
const MAX_BIO = 2000;
const MAX_URL = 500;

function buildInitialDraft(profile, firebaseUser) {
  return {
    display_name:
      (profile && profile.display_name) ||
      (firebaseUser && firebaseUser.displayName) ||
      '',
    org_type: (profile && profile.org_type) || ORG_TYPE.INDIVIDUAL,
    website_url: (profile && profile.website_url) || '',
    bio: (profile && profile.bio) || '',
    photo_url:
      (profile && profile.photo_url) ||
      (firebaseUser && firebaseUser.photoURL) ||
      '',
  };
}

const OnboardingWizard = ({ open, onClose, onComplete }) => {
  const { profile, user, refreshProfile, markOnboardingComplete } = useAuth();
  const { project } = useProject();

  const projectLabel = useMemo(
    () =>
      project?.project_name
        ? `MAP250 \u00B7 ${project.project_name.toUpperCase()} PROJECT`
        : 'MAP250',
    [project]
  );

  const [draft, setDraft] = useState(() => buildInitialDraft(profile, user));
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const step = STEPS[stepIdx];
  const isFinalStep = stepIdx === STEPS.length - 1;

  const setField = useCallback((key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setFieldErrors((fe) =>
      fe[key] ? { ...fe, [key]: undefined } : fe
    );
  }, []);

  // Mirror updateUserProfile.php's validation so we don't make the user
  // wait for a server round-trip to learn their URL is malformed.
  const validate = useCallback(() => {
    const errs = {};
    if (draft.display_name && draft.display_name.length > MAX_NAME) {
      errs.display_name = `Must be ${MAX_NAME} characters or fewer.`;
    }
    if (draft.bio && draft.bio.length > MAX_BIO) {
      errs.bio = `Must be ${MAX_BIO} characters or fewer.`;
    }
    if (draft.website_url) {
      if (draft.website_url.length > MAX_URL) {
        errs.website_url = `Must be ${MAX_URL} characters or fewer.`;
      } else if (!/^https?:\/\//i.test(draft.website_url)) {
        errs.website_url = 'Must start with http:// or https://';
      }
    }
    if (draft.org_type && !Object.values(ORG_TYPE).includes(draft.org_type)) {
      errs.org_type = 'Pick one.';
    }
    return errs;
  }, [draft]);

  const handleNext = useCallback(() => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  }, [validate]);

  const handleBack = useCallback(() => {
    setStepIdx((i) => Math.max(i - 1, 0));
  }, []);

  // PATCH the editable fields and bubble up. We always send the full
  // editable payload (the server ignores keys it doesn't recognize and
  // accepts empty strings as "clear", which is what we want for
  // skipped-but-touched fields).
  const persistAndClose = useCallback(
    async ({ markComplete }) => {
      setError(null);
      setSubmitting(true);
      try {
        // Only include fields the user actually entered (truthy or non-empty
        // explicit blanks for already-stored data). We send empty string
        // for bio/website to allow clearing, but we don't want to overwrite
        // a Firebase-provided display_name with "" if the user truly didn't
        // touch it. Heuristic: if the field is non-empty OR the user typed
        // a blank where there used to be content, send it; otherwise omit.
        const payload = {};
        const init = buildInitialDraft(profile, user);
        Object.keys(draft).forEach((k) => {
          if (draft[k] !== init[k]) payload[k] = draft[k];
        });
        // On finish, also stamp org_type if it wasn't sent (we default to
        // 'individual' for new users, per spec). This makes sure the
        // server sees a value and clears the NULL state so future syncs
        // don't second-guess it.
        if (markComplete && !('org_type' in payload) && !init.org_type) {
          payload.org_type = draft.org_type || ORG_TYPE.INDIVIDUAL;
        }

        if (Object.keys(payload).length > 0) {
          await apiCall('updateUserProfile.php', {
            method: 'POST',
            body: payload,
          });
          await refreshProfile();
        }

        if (markComplete) {
          markOnboardingComplete();
        } else {
          // Skip-without-changes path: still flip the local flag so we
          // don't remount immediately, but DON'T claim the user finished —
          // server-side onboarded_at remains NULL and we'll re-prompt
          // on next login.
          markOnboardingComplete();
        }
        onComplete?.({ skipped: !markComplete });
        onClose?.();
      } catch (e) {
        const msg =
          e instanceof ApiError
            ? e.message
            : (e && e.message) || 'Could not save profile. Please try again.';
        setError(msg);
      } finally {
        setSubmitting(false);
      }
    },
    [draft, profile, user, refreshProfile, markOnboardingComplete, onComplete, onClose]
  );

  const handleSkip = useCallback(() => {
    // Skip wraps up without trying to PATCH; we still want a clean exit so
    // the wizard doesn't immediately reappear.
    persistAndClose({ markComplete: false });
  }, [persistAndClose]);

  const handleFinish = useCallback(() => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    persistAndClose({ markComplete: true });
  }, [validate, persistAndClose]);

  // Photo upload — fires immediately on file pick. We don't write to
  // users.photo_url until Finish, so cancelling the wizard doesn't leave
  // a half-set photo on the user. We *do* upload to GCS though; that's
  // an acceptable trade because the file is harmless if orphaned.
  const handlePhotoChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploadingPhoto(true);
      setError(null);
      try {
        const formData = new FormData();
        formData.append('upload_target', 'profile_photo');
        formData.append('files[]', file);
        const resp = await apiCall('uploadFiles.php', {
          method: 'POST',
          body: formData,
        });
        const url = resp?.files?.[0]?.filepath;
        if (!url) {
          throw new Error('Upload completed but no URL was returned.');
        }
        setField('photo_url', url);
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? err.message
            : (err && err.message) || 'Photo upload failed. Please try a different image.';
        setError(msg);
      } finally {
        setUploadingPhoto(false);
        // Reset the input so picking the same file twice still fires onChange.
        if (e.target) e.target.value = '';
      }
    },
    [setField]
  );

  const renderIdentity = () => (
    <>
      <div className="onboarding-wizard__field">
        <label className="onboarding-wizard__label" htmlFor="wiz-name">
          Name
        </label>
        <input
          id="wiz-name"
          className="onboarding-wizard__input"
          type="text"
          maxLength={MAX_NAME}
          value={draft.display_name}
          onChange={(e) => setField('display_name', e.target.value)}
          placeholder="Your name or your organization's name"
        />
        {fieldErrors.display_name && (
          <span className="onboarding-wizard__error">{fieldErrors.display_name}</span>
        )}
      </div>

      <div className="onboarding-wizard__field">
        <label className="onboarding-wizard__label" htmlFor="wiz-org-type">
          Type of contributor
        </label>
        <select
          id="wiz-org-type"
          className="onboarding-wizard__select"
          value={draft.org_type || ORG_TYPE.INDIVIDUAL}
          onChange={(e) => setField('org_type', e.target.value)}
        >
          {ORG_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {fieldErrors.org_type && (
          <span className="onboarding-wizard__error">{fieldErrors.org_type}</span>
        )}
      </div>

      <div className="onboarding-wizard__field">
        <label className="onboarding-wizard__label" htmlFor="wiz-website">
          Website (optional)
        </label>
        <input
          id="wiz-website"
          className="onboarding-wizard__input"
          type="url"
          maxLength={MAX_URL}
          value={draft.website_url}
          onChange={(e) => setField('website_url', e.target.value)}
          placeholder="https://example.com"
        />
        {fieldErrors.website_url && (
          <span className="onboarding-wizard__error">{fieldErrors.website_url}</span>
        )}
      </div>
    </>
  );

  const renderBio = () => (
    <div className="onboarding-wizard__field">
      <label className="onboarding-wizard__label" htmlFor="wiz-bio">
        About you
      </label>
      <textarea
        id="wiz-bio"
        className="onboarding-wizard__textarea"
        maxLength={MAX_BIO}
        value={draft.bio}
        onChange={(e) => setField('bio', e.target.value)}
        placeholder={PROMPT_PLACEHOLDER}
      />
      <span className="onboarding-wizard__hint">
        {draft.bio.length}/{MAX_BIO}
      </span>
      {fieldErrors.bio && (
        <span className="onboarding-wizard__error">{fieldErrors.bio}</span>
      )}
    </div>
  );

  const renderPhoto = () => (
    <>
      <div className="onboarding-wizard__photo-row">
        {draft.photo_url ? (
          <img
            className="onboarding-wizard__photo-preview"
            src={draft.photo_url}
            alt="Profile preview"
          />
        ) : (
          <span className="onboarding-wizard__photo-preview" aria-hidden="true">
            no&nbsp;photo
          </span>
        )}
        <div className="onboarding-wizard__photo-actions">
          <label className="onboarding-wizard__file-button">
            {uploadingPhoto ? 'Uploading…' : draft.photo_url ? 'Replace photo' : 'Upload photo'}
            <input
              className="onboarding-wizard__file-input"
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              disabled={uploadingPhoto}
            />
          </label>
          {draft.photo_url && (
            <button
              type="button"
              className="onboarding-wizard__file-button"
              onClick={() => setField('photo_url', '')}
              disabled={uploadingPhoto}
            >
              Remove
            </button>
          )}
        </div>
      </div>
      <p className="onboarding-wizard__hint">
        Square images look best. We crop to a circle for the avatar.
      </p>
    </>
  );

  const renderConfirm = () => {
    const orgLabel =
      ORG_TYPE_OPTIONS.find((o) => o.value === draft.org_type)?.label ||
      'Individual';
    return (
      <div className="onboarding-wizard__field">
        <div className="onboarding-wizard__photo-row">
          {draft.photo_url ? (
            <img
              className="onboarding-wizard__photo-preview"
              src={draft.photo_url}
              alt="Profile preview"
            />
          ) : (
            <span className="onboarding-wizard__photo-preview" aria-hidden="true">
              no&nbsp;photo
            </span>
          )}
          <div>
            <div style={{ fontWeight: 700 }}>
              {draft.display_name || '(no name)'}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#6b665e' }}>
              {orgLabel}
              {draft.website_url ? ` \u00B7 ${draft.website_url}` : ''}
            </div>
          </div>
        </div>
        {draft.bio && (
          <p
            style={{
              margin: 0,
              fontSize: '0.88rem',
              color: 'var(--theme-color)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {draft.bio}
          </p>
        )}
      </div>
    );
  };

  const renderStep = () => {
    switch (step) {
      case 'identity':
        return renderIdentity();
      case 'bio':
        return renderBio();
      case 'photo':
        return renderPhoto();
      case 'confirm':
        return renderConfirm();
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        // Backdrop click shouldn't accidentally finish the wizard. Treat
        // it as Skip so we still mark the local flag and dismiss cleanly.
        if (!submitting && !uploadingPhoto) handleSkip();
      }}
      closeAfterTransition
      slotProps={{ backdrop: { timeout: 400 } }}
      className="onboarding-wizard-overlay"
      aria-labelledby="onboarding-wizard-heading"
    >
      <Fade in={open} timeout={400}>
        <div className="onboarding-wizard" role="dialog">
          <div className="onboarding-wizard__body">
            <p className="onboarding-wizard__project-label">{projectLabel}</p>
            <p className="onboarding-wizard__step-indicator">
              Step {stepIdx + 1} of {STEPS.length}
            </p>
            <h2 id="onboarding-wizard-heading" className="onboarding-wizard__heading">
              {STEP_LABELS[step]}
            </h2>
            <p className="onboarding-wizard__description">{STEP_DESCRIPTIONS[step]}</p>

            {error && <p className="onboarding-wizard__form-error">{error}</p>}

            {renderStep()}

            <div className="onboarding-wizard__buttons">
              {stepIdx > 0 ? (
                <button
                  type="button"
                  className="onboarding-wizard__btn onboarding-wizard__btn--back"
                  onClick={handleBack}
                  disabled={submitting || uploadingPhoto}
                >
                  Back
                </button>
              ) : (
                <span style={{ flex: 1 }} aria-hidden="true" />
              )}
              {isFinalStep ? (
                <button
                  type="button"
                  className="onboarding-wizard__btn onboarding-wizard__btn--finish"
                  onClick={handleFinish}
                  disabled={submitting || uploadingPhoto}
                >
                  {submitting ? 'Saving…' : 'Finish'}
                </button>
              ) : (
                <button
                  type="button"
                  className="onboarding-wizard__btn onboarding-wizard__btn--next"
                  onClick={handleNext}
                  disabled={submitting || uploadingPhoto}
                >
                  Next
                </button>
              )}
            </div>
          </div>

          <div className="onboarding-wizard__footer">
            <div className="onboarding-wizard__divider" />
            <button
              type="button"
              className="onboarding-wizard__skip"
              onClick={handleSkip}
              disabled={submitting || uploadingPhoto}
            >
              Skip for now
            </button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default OnboardingWizard;
